import React from "react";
import { Upload, RotateCcw, Loader2 } from "lucide-react";
import { apiUploadLogo, apiResetLogo, formatApiErrorDetail } from "@/lib/api";
import { Logo, useBranding, muatUlangBranding } from "@/components/Logo";

const VARIAN = [
  ["putih", "Logo putih", "Untuk latar gelap: aplikasi, login, TV, tracking.", "bg-slate-950"],
  ["hitam", "Logo hitam", "Untuk halaman putih: nota digital, halaman customer, halaman ACC.", "bg-white"],
];

// G1-B: Owner replaces the default logo kit (server re-encodes as PNG).
export default function LogoPanel() {
  const info = useBranding();
  const [busy, setBusy] = React.useState(null);
  const [msg, setMsg] = React.useState(null);

  const run = async (varian, fn, ok) => {
    setBusy(varian);
    setMsg(null);
    try {
      await fn();
      await muatUlangBranding();
      setMsg({ ok: true, text: ok });
    } catch (e) {
      setMsg({ ok: false, text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="logo-panel" className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-4">
      <div>
        <h2 className="text-[15px] font-bold text-soq-biru-tua">Logo</h2>
        <p className="text-xs text-slate-500 mt-1">
          Tanpa upload, logo bawaan SOQPRINT dipakai. File PNG/JPG/WebP maks 5 MB; server memperkecil dan membuang metadata. Setiap perubahan tercatat di audit log.
        </p>
      </div>
      {msg && (
        <div data-testid="logo-msg" className={`text-sm ${msg.ok ? "text-emerald-300" : "text-red-300"}`}>{msg.text}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {VARIAN.map(([v, judul, ket, bg]) => (
          <div key={v} className="rounded-lg border border-slate-800 p-4 space-y-3" data-testid={`logo-slot-${v}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-200">{judul}</span>
              <span className="text-[10px] font-mono uppercase text-slate-500">{info?.[v] ? `upload v${info[v]}` : "bawaan"}</span>
            </div>
            <div className={`${bg} rounded-md p-4 flex items-center justify-center h-24`}>
              <Logo varian={v} className="max-h-16 w-auto" />
            </div>
            <p className="text-xs text-slate-500">{ket}</p>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-amber-500/40 border-l-4 border-l-amber-500 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300 hover:bg-amber-500/20">
                {busy === v ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Upload logo
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid={`logo-file-${v}`}
                  disabled={!!busy}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) run(v, () => apiUploadLogo(v, f), `${judul} diperbarui.`);
                  }}
                />
              </label>
              {info?.[v] && (
                <button
                  type="button"
                  data-testid={`logo-reset-${v}`}
                  disabled={!!busy}
                  onClick={() => window.confirm(`Kembalikan ${judul.toLowerCase()} ke logo bawaan?`) && run(v, () => apiResetLogo(v), `${judul} kembali ke bawaan.`)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:text-slate-100"
                >
                  <RotateCcw size={14} /> Kembali ke bawaan
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
